import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { filterAudioBySearchValue } from './helpers'

import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { audioSelectors } from 'store/features/audio/selectors'
import { audioThunks } from 'store/features/audio/thunks'
import { IUserAudio } from 'store/types/audio'
import { MUSIC_LIBRARY, MY_MUSIC } from '../content/data'

interface IAudioContentContext {
  musicSearchValue: string
  setMusicSearchValue: (value: string) => void
  isShowMusicSearchView: boolean
  isAudioSearchLoading: boolean
  setIsAudioSearchLoading: (value: boolean) => void
  searchedMusicList: IUserAudio[]
  musicActiveTabName: string
  setMusicActiveTabName: (value: string) => void
  goToMyMusic: () => void
}

export const AudioContentContext = createContext<IAudioContentContext>({
  musicSearchValue: '',
  setMusicSearchValue: () => {},
  isShowMusicSearchView: false,
  isAudioSearchLoading: false,
  setIsAudioSearchLoading: () => {},
  searchedMusicList: [],
  musicActiveTabName: MUSIC_LIBRARY,
  setMusicActiveTabName: () => {},
  goToMyMusic: () => {}
})

export const AudioContentProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [musicSearchValue, setMusicSearchValue] = useState('')
  const [isAudioSearchLoading, setIsAudioSearchLoading] = useState(false)
  const [musicActiveTabName, setMusicActiveTabName] = useState(MUSIC_LIBRARY)
  // Track the query the lib results belong to so we never render stale hits
  // from a previous search while a new fetch is still in flight.
  const [libSearch, setLibSearch] = useState<{
    query: string
    results: IUserAudio[]
  }>({ query: '', results: [] })

  const userSounds = useSelector(audioSelectors.userSounds)

  useEffect(() => {
    if (!musicSearchValue || musicActiveTabName === MY_MUSIC) return

    let cancelled = false
    setIsAudioSearchLoading(true)

    dispatch(audioThunks.searchLibSounds(musicSearchValue))
      .then((results: IUserAudio[]) => {
        if (!cancelled) setLibSearch({ query: musicSearchValue, results })
      })
      .catch(() => {
        if (!cancelled) setLibSearch({ query: musicSearchValue, results: [] })
      })
      .finally(() => {
        if (!cancelled) setIsAudioSearchLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [musicSearchValue, musicActiveTabName])

  const searchedMusicList = useMemo<IUserAudio[]>(() => {
    if (!musicSearchValue) return []
    if (musicActiveTabName === MY_MUSIC) {
      return filterAudioBySearchValue(userSounds, musicSearchValue)
    }
    // Library tab — only render results once they correspond to the current query.
    return libSearch.query === musicSearchValue ? libSearch.results : []
  }, [musicSearchValue, musicActiveTabName, userSounds, libSearch])

  const isShowMusicSearchView = !!musicSearchValue || isAudioSearchLoading

  const goToMyMusic = useCallback(() => {
    setMusicActiveTabName(MY_MUSIC)
    if (musicSearchValue) {
      setMusicSearchValue('')
    }
  }, [setMusicActiveTabName, musicSearchValue, setMusicSearchValue])

  return (
    <AudioContentContext.Provider
      value={{
        musicSearchValue,
        setMusicSearchValue,
        isShowMusicSearchView,
        isAudioSearchLoading,
        setIsAudioSearchLoading,
        searchedMusicList,
        musicActiveTabName,
        setMusicActiveTabName,
        goToMyMusic
      }}
    >
      {children}
    </AudioContentContext.Provider>
  )
}
